/**
 * /ninety/projects — My Day · Monthly Projects.
 *
 * VISUAL REFERENCE build for the development team: reads and writes the fake
 * in-memory store in ~/components/myday/store.ts. Search, sort, team chips,
 * "Show done", status cycling (click the pill), expand-to-milestones, milestone
 * check-off (progress updates live), add milestone, create, edit and delete all
 * work in-session and reset on reload.
 */
import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { cn } from '~/lib/cn';
import { useMyDay, ME_ID, TEAMS, type MonthlyProject } from '~/components/myday/store';
import {
  Panel, PanelToolbar, SearchBox, ToolbarSelect, ClearButton, TeamChips, Switch,
  NewButton, ToolbarActions, PagerFooter, EmptyState, FakeDataNote, IconGem,
} from '~/components/myday/ui';
import { ProjectRow, PROJECT_GRID } from '~/components/myday/projects/ProjectRow';
import { ProjectDialog } from '~/components/myday/projects/ProjectDialog';
import { STATUS_ORDER, progressPct } from '~/components/myday/projects/status';

export const Route = createFileRoute('/_app/ninety/projects')({
  component: ProjectsTab,
});

type SortKey = 'due' | 'status' | 'title' | 'progress';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'due', label: 'Sort: Due date' },
  { value: 'status', label: 'Sort: Status' },
  { value: 'title', label: 'Sort: Title' },
  { value: 'progress', label: 'Sort: Progress' },
];

function ProjectsTab() {
  const projects = useMyDay((s) => s.projects);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('due');
  const [team, setTeam] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [pageSize, setPageSize] = useState(50);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  // undefined = closed, null = new, project = editing
  const [editing, setEditing] = useState<MonthlyProject | null | undefined>(undefined);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = projects.filter((p) => {
      if (team && p.team !== team) return false;
      if (!showDone && p.status === 'done') return false;
      if (q && !`${p.title} ${p.detail} ${p.milestones.map((m) => m.title).join(' ')}`.toLowerCase().includes(q)) return false;
      return true;
    });
    const due = (p: MonthlyProject) => new Date(p.dueAt).getTime();
    return [...rows].sort((a, b) => {
      switch (sort) {
        case 'status': return STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) || due(a) - due(b);
        case 'title': return a.title.localeCompare(b.title);
        case 'progress': return progressPct(b) - progressPct(a) || due(a) - due(b);
        default: return due(a) - due(b);
      }
    });
  }, [projects, search, sort, team, showDone]);

  const visible = filtered.slice(0, pageSize);

  const toggle = (id: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const clear = () => {
    setSearch(''); setSort('due'); setTeam(null); setShowDone(false);
  };

  return (
    <div className="px-4 sm:px-6 py-4">
      <Panel>
        <PanelToolbar>
          <div className="flex flex-wrap items-center gap-2">
            <SearchBox value={search} onChange={setSearch} placeholder="Search monthly projects..." />
            <ToolbarSelect value={sort} onChange={setSort} options={SORT_OPTIONS} />
            <ClearButton onClick={clear} />
          </div>
          <TeamChips value={team} onChange={setTeam} count={filtered.length} />
          <ToolbarActions>
            <Switch checked={showDone} onChange={setShowDone} label="Show done" />
            <NewButton onClick={() => setEditing(null)} />
          </ToolbarActions>
        </PanelToolbar>

        <div className="overflow-x-auto">
          <div className="min-w-[820px]">
            <div className={cn(PROJECT_GRID, 'py-2 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-500')}>
              <span />
              <span>Status</span>
              <span>Title</span>
              <span>Milestone progress</span>
              <span>Due by</span>
              <span />
            </div>

            {visible.length === 0 ? (
              <EmptyState icon={<IconGem className="w-8 h-8" />} title="No monthly projects"
                body="Nothing matches these filters. Clear them or add a new monthly project." />
            ) : (
              visible.map((p) => (
                <ProjectRow key={p.id} project={p} expanded={expanded.has(p.id)}
                  onToggle={() => toggle(p.id)} onEdit={() => setEditing(p)} />
              ))
            )}

            <div className="px-4 py-3">
              <button type="button" onClick={() => setEditing(null)}
                className="text-sm font-semibold hover:underline" style={{ color: '#ea580c' }}>
                + Add monthly project
              </button>
            </div>
          </div>
        </div>

        <PagerFooter pageSize={pageSize} onPageSize={setPageSize} shown={visible.length} total={filtered.length} />
      </Panel>
      <FakeDataNote />

      {editing !== undefined && (
        <ProjectDialog project={editing} onClose={() => setEditing(undefined)}
          defaults={{ team: team ?? TEAMS[0], ownerId: ME_ID }} />
      )}
    </div>
  );
}
